import angular from 'angular';
import jsCookie from 'js-cookie';
/**
 * This service is in charge of managing the SSO implementation settings received by the app.
 *
 * The state is a set of settings the implementation sends that determine things like the type of
 * authentication, whether the user needs to select an account or not, if the user needs an
 * application permissions, etc.
 * When the app receives the state, it uses this service to store it on a cookie until the
 * authentication process finishes. The reason is stored on a cookie is because the process may
 * include redireccions and it also needs to be available on _"private mode"_ navigation.
 * @ngesdoc service
 * @ngname ssoState
 * @ngmodule auth
 * @ngrequires {@link jsCookie}
 */
class SSOState {
  /**
   * @param {FrontendConfiguration} appConfiguration To read the cookie settings, the version of
   *                                                 the app and the options for the default
   *                                                 implementation.
   */
  constructor(appConfiguration) {
    'ngInject';

    /**
     * The app version, to save it on the cookie when it gets generated.
     * @type {string}
     * @access protected
     * @ignore
     */
    this._version = appConfiguration.version;
    /**
     * The name of the cookie that will store the state.
     * @type {string}
     * @access protected
     * @ignore
     */
    this._cookieName = appConfiguration.features.cookies.state;
    /**
     * The list of properties the cookie will store. This is defined as a property in order to
     * _"sanitize"_ the recevied parameters from the implementation.
     * @type {Array}
     * @access protected
     * @ignore
     */
    this._stateProperties = [
      'client',
      'application',
      'applicationPermissionsRequired',
      'accountSelection',
      'accountScope',
      'accountId',
      'callback',
      'fallback',
      'creatorToken',
    ];
    /**
     * The settings for the fallback implementation. They'll be used to create a default state if
     * the app doesn't receive parameters from the SSO client.
     * @type {SSOSTateSettings}
     * @access protected
     * @ignore
     */
    this._fallback = appConfiguration.features.fallbackImplementation;
    /**
     * The settings for the Home implementation. They'll be used to create a default state if
     * the app doesn't receive parameters from the SSO client.
     * @type {SSOSTateSettings}
     * @access protected
     * @ignore
     */
    this._home = appConfiguration.features.homeImplementation;
    /**
     * The current state.
     * @type {?SSOSTateSettings}
     * @access protected
     * @ignore
     */
    this._state = null;
  }
  /**
   * Creates a state with the settings from the fallback implementation. If the authentication
   * process is started without any parameters, the app will assume that the user is trying to log
   * in on the SSO server it self, so in order to avoid that, we create a state with the parameters
   * of a known implementation.
   * @return {SSOSTateSettings}
   */
  createDefaultState() {
    return this.createState(Object.assign({}, this._fallback, { fallback: true }));
  }
  /**
   * Creates a state from a dictionary. The dictionary may contain all sort of properties, but
   * the method will only use those that the service recognizes as _"state properties"_ (defined
   * on a protected property).
   * @param {Object} params A dictionary of parameters that will be filtered and used to create a
   *                        new state.
   * @return {SSOSTateSettings}
   */
  createState(params) {
    // Define the object for the new state.
    const state = {};
    // Sanitize the params.
    this._stateProperties.forEach((name) => {
      const value = params[name];
      if (angular.isDefined(value)) {
        state[name] = value;
      }
    });
    // Write the cookie.
    jsCookie.set(
      this._cookieName,
      {
        version: this._version,
        state,
      },
      {
        secure: true,
      },
    );
    // Update the local property.
    this._state = state;
    // Return the new state.
    return this.state;
  }
  /**
   * Destroys the current state (sets it to `null`) and deletes the cookie.
   */
  destroyState() {
    this._state = {};
    jsCookie.remove(this._cookieName);
  }
  /**
   * Gets the current state. If the service doesn't have one, it will try to load it from the
   * cookie (with {@link SSOState#restoreState}).
   * @return {SSOSTateSettings}
   */
  getState() {
    if (!this._state) {
      this.restoreState();
    }

    return this._state;
  }
  /**
   * Tries to load an already saved store from the cookie. If there's a cookie and it has a state,
   * it will validate that the cookie was created with the same version the app is currently
   * running with. If the versions are different, the cookie will be deleted and the state won't
   * be restored.
   * @return {boolean} Whether the state could be restored or not.
   */
  restoreState() {
    let success = true;
    const saved = jsCookie.getJSON(this._cookieName);
    if (saved) {
      if (saved.version !== this._version) {
        success = false;
        this.destroyState();
      } else if (saved.state) {
        this._state = saved.state;
      }
    }

    return success;
  }
  /**
   * Overwrites the state settings with the ones for the Home implementation. This is used when
   * the authentication process is started without any paramters, the app will assume that the
   * user is trying to log in on the SSO server itself and in order to avoid that, it will set
   * the fallback implementation as state; but later, when the user enters his/her email address
   * and the settings for the domain are retrieved, if the user domain has the Home enabled and
   * the current state is the one for the fallback, this method will change the settings so the
   * user can be redirected to the Home.
   * @return {SSOSTateSettings} If the current state is the one for the fallback implementation,
   *                            it will return the new state with the Home settings; but if not,
   *                            then it will return the state as it is.
   */
  useHomeState() {
    return this._state && this._state.fallback ?
      this.createState(this._home) :
      this.getState();
  }
}

export default SSOState;
